import { forwardRef, useState, useEffect, useImperativeHandle, useRef } from "react";
import cn from "../../../utils/cn";
import { get, isArray, isFunction } from "lodash";
import PropTypes from "prop-types";
import Overlay from "./Overlay";
import OverlaySpinner from "./OverlaySpinner";
import Icon from "../Icon/Icon";
import FocusTrap from "focus-trap-react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Title = _ref => {
  let {
    title,
    close,
    removeCloseIcon,
    titleStyles,
    dataTestElementClose
  } = _ref;
  if (!title) {
    return null;
  }
  const titleClass = cn(titleStyles, `p-5 border-b border-solid border-dark-border-grey flex-2 box-border text-xl leading-4 font-regular-font`);
  const titleCloseableClass = cn(titleClass, "flex", "justify-between", "items-center");
  if (removeCloseIcon) {
    return /*#__PURE__*/_jsx("div", {
      className: titleClass,
      children: title
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: titleCloseableClass,
    "data-test-element": dataTestElementClose,
    children: [title, /*#__PURE__*/_jsx(Icon, {
      icon: "Cross",
      ariaLabel: `Close ${title} Modal`,
      onClick: close,
      size: 16,
      tabIndex: "0"
    })]
  });
};
Title.propTypes = {
  title: PropTypes.node,
  close: PropTypes.func,
  removeCloseIcon: PropTypes.bool,
  titleStyles: PropTypes.string,
  dataTestElementClose: PropTypes.string
};
const Footer = _ref2 => {
  let {
    actions,
    footerStyles
  } = _ref2;
  if (!actions) {
    return null;
  }
  const actionsClass = cn(footerStyles, "border-t border-solid border-dark-border-grey p-4 box-border text-right flex-3");
  const actionItemsClass = "mr-4 inline-block mr-0:last-child";
  if (isArray(actions)) {
    return /*#__PURE__*/_jsx("div", {
      className: actionsClass,
      children: actions.map((a, i) => /*#__PURE__*/_jsx("span", {
        className: actionItemsClass,
        children: a
      }, get(a, "props.id", i)))
    });
  }
  return /*#__PURE__*/_jsx("div", {
    className: actionsClass,
    children: actions
  });
};
Footer.propTypes = {
  actions: PropTypes.node,
  footerStyles: PropTypes.string
};
const Modal = /*#__PURE__*/forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [resolver, setResolver] = useState(() => null);
  const initialRender = useRef({
    renderOpen: false,
    renderResolve: false
  });
  const {
    actions,
    children,
    id,
    removeCloseIcon,
    spinnerShow,
    spinnerText,
    spinnerDescription,
    size,
    titleStyles,
    footerStyles,
    title,
    modalContentStyles,
    showBorderRadius,
    customStyle,
    dataTestElementClose,
    className,
    customSize,
    "data-testid": dataId,
    disableScroll = false,
    modal
  } = props;
  useEffect(() => {
    if (typeof document === "undefined") return;
    const firstNameInput = document.getElementById("first-name");
    if (firstNameInput) {
      setTimeout(() => {
        firstNameInput.focus();
      }, 100);
    }
  }, []);
  useEffect(() => {
    if (initialRender.current.renderOpen) {
      setOpen(true);
    }
    initialRender.current.renderOpen = true;
  }, [resolver]);
  useEffect(function () {
    let action = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "dismiss";
    if (initialRender.current.renderResolve) {
      setResolver(action);
    }
    initialRender.current.renderResolve = true;
  }, [open]);
  const modalSizeObject = {
    mini: "w-275 min-h-0",
    small: "w-400",
    normal: "w-600",
    large: "w-800",
    jumbo: "w-1000",
    full: "w-9/10"
  };
  const modalSize = size in modalSizeObject ? modalSizeObject[size] : modalSizeObject.normal;
  const openPaperClass = `transform -translate-x-1/2 -translate-y-1/2 z-1500 top-1/2 left-1/2 opacity-100 text-text-blue bg-white transition-all duration-300 delay-0 ease-in-out
  box-border font-regular-font shadow-modal rounded-2 max-h-90vh fixed max-w-9/10 flex flex-col flex-no-wrap`;
  const dialogPaperClass = cn(openPaperClass, modalSize, {
    rounded: showBorderRadius
  });
  const modalContent = cn(modalContentStyles, "flex-4 py-5 px-5 text-base w-full box-border max-h-90vh relative", {
    "overflow-y-auto": !disableScroll
  });
  let onRequestClose = () => null;
  const onEscape = e => {
    if (e.key === "Escape" && !modal) {
      onRequestClose();
    }
  };
  const closeModal = () => {
    if (typeof document === "undefined") return;
    document.removeEventListener("keyup", onEscape);
    setOpen(false);
  };
  onRequestClose = () => {
    if (isFunction(props.onRequestClose)) {
      return props.onRequestClose();
    }
    return closeModal();
  };
  const onOverlayClick = () => {
    if (!modal) {
      onRequestClose();
    }
  };
  const getModalClass = () => cn({
    dialog: true,
    open
  });
  const openModal = () => {
    if (typeof document !== "undefined") {
      document.addEventListener("keyup", onEscape);
    }
    return new Promise(resolve => {
      setResolver(resolve);
      if (props.focusTrap) {
        focusTrap();
      }
    });
  };
  useImperativeHandle(ref, () => ({
    openModal() {
      openModal();
    },
    closeModal() {
      closeModal();
    }
  }), []);
  const modalStyle = {
    ...customSize,
    ...(customStyle || {})
  };
  return /*#__PURE__*/_jsxs("div", {
    id: id,
    className: cn(className, `${getModalClass()}`, customSize ? "custom" : ""),
    "data-testid": dataId,
    hidden: !open,
    role: "dialog",
    "aria-modal": "true",
    children: [/*#__PURE__*/_jsx(Overlay, {
      open: open,
      autoLockScrolling: true,
      onClick: onOverlayClick
    }), /*#__PURE__*/_jsx(FocusTrap, {
      active: open,
      focusTrapOptions: {
        escapeDeactivates: true,
        allowOutsideClick: false,
        fallbackFocus: '#modal-fallback'
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: dialogPaperClass,
        style: modalStyle,
        children: [/*#__PURE__*/_jsx("button", {
          id: "modal-fallback",
          style: {
            display: 'none'
          },
          tabIndex: "-1"
        }), /*#__PURE__*/_jsx(OverlaySpinner, {
          show: spinnerShow,
          text: spinnerText,
          description: spinnerDescription
        }), /*#__PURE__*/_jsx(Title, {
          title: title,
          close: onRequestClose,
          removeCloseIcon: removeCloseIcon,
          titleStyles: titleStyles,
          dataTestElementClose: dataTestElementClose
        }), /*#__PURE__*/_jsx("div", {
          className: modalContent,
          children: children
        }), /*#__PURE__*/_jsx(Footer, {
          footerStyles: footerStyles,
          actions: actions
        })]
      })
    })]
  });
});
Modal.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  customSize: PropTypes.object,
  dataTestId: PropTypes.string,
  footerStyles: PropTypes.string,
  focusTrap: PropTypes.bool,
  id: PropTypes.string,
  modal: PropTypes.bool,
  modalContentStyles: PropTypes.string,
  onRequestClose: PropTypes.func,
  removeCloseIcon: PropTypes.bool,
  showBorderRadius: PropTypes.bool,
  size: PropTypes.oneOf(["mini", "small", "normal", "large", "jumbo", "full"]),
  spinnerDescription: PropTypes.string,
  spinnerShow: PropTypes.bool,
  spinnerText: PropTypes.string,
  title: PropTypes.node,
  titleStyles: PropTypes.string,
  "data-testid": PropTypes.string,
  disableScroll: PropTypes.bool
};
Modal.displayName = 'Modal';
export default Modal;
//# sourceMappingURL=Modal.js.map